// ============================================================
// The terrain's faces, their arrival, and the warning marks
// ============================================================

import { useRef } from 'react';
import type { SceneFace } from '../../engine/battlefield/scene';
import { SCENE_VIEW } from '../../engine/battlefield/projection';
import { arrivalDelays } from '../../engine/battlefield/terrainAnimation';
import type { TerrainAnimation } from '../../engine/battlefield/terrainAnimation';
import clsx from '../../utils/clsx';
import styles from '../../styles/Battlefield.module.css';
import { toPoints, faceHitAttrs } from './sceneDrawing';
import { faceFill, faceOpacity } from '../../engine/battlefield/faceColour';

/**
 * The motion each arrival wears.
 *
 * Spelled out rather than derived from the key by string-building: a
 * missing CSS Module class comes back `undefined` and fails silently, so
 * an explicit map is what makes a typo a type error instead of a face
 * that quietly never animates.
 */
export const arrivalClasses = (s: Record<string, string>): Record<TerrainAnimation, string | undefined> => ({
  none: undefined,
  sweep: s.arriveSweep,
  rise: s.arriveRise,
  bloom: s.arriveBloom,
  approach: s.arriveApproach,
  scatter: s.arriveScatter,
});

const MOTION = arrivalClasses(styles);

type Ledger = Map<string, number>;

/** Give every face that has just appeared the delay it will keep. */
function recordFresh(ledger: Ledger, faces: SceneFace[], animation: TerrainAnimation) {
  const fresh = faces.filter((face) => !ledger.has(face.key));
  if (!fresh.length) return;
  const delays = arrivalDelays(fresh, animation, SCENE_VIEW);
  for (const face of fresh) ledger.set(face.key, delays.get(face.key) ?? 0);
}

/**
 * Forget the faces that have gone, or a long session of building and
 * erasing grows a ledger of keys nothing will ever ask about again.
 */
function pruneGone(ledger: Ledger, faces: SceneFace[]) {
  if (ledger.size <= faces.length) return;
  const live = new Set(faces.map((face) => face.key));
  for (const key of ledger.keys()) if (!live.has(key)) ledger.delete(key);
}

/**
 * WHEN EACH FACE ARRIVED, and therefore which ones are arriving NOW.
 *
 * A face that is already on screen must keep the delay it mounted with:
 * changing `animation-delay` on an animation that has finished can set it
 * running again, and the whole map would re-resolve every time anything
 * changed. So the ledger is written once per key and never revised.
 *
 * It doubles as the arrival detector - anything absent from it is new -
 * which is what lets the ordering be computed over just the faces that are
 * appearing. That is the whole of how a brush stroke stays quick while a
 * map load takes the full sweep: see `spreadFor`.
 */
export function useArrivalLedger(faces: SceneFace[], animation: TerrainAnimation): Ledger {
  const arrived = useRef<Ledger>(new Map());
  if (animation !== 'none') {
    recordFresh(arrived.current, faces, animation);
    pruneGone(arrived.current, faces);
  }
  // Nothing is owed a delay, and the ledger must not carry stale ones into
  // the next animation the viewer picks.
  else if (arrived.current.size) arrived.current = new Map();
  return arrived.current;
}

interface SceneFacesProps {
  faces: SceneFace[];
  selectedSolidId: string | null;
  animation: TerrainAnimation;
}

/**
 * Every face, as a polygon. `data-solid-id` is read by the hover readout:
 * the browser's own hit test is exact and already depth-ordered, which no
 * ground-plane probe can be for a face standing three metres up.
 *
 * THE SWEEP is a delay per face. `face.key` is stable per solid per face,
 * so React mounts a node only when terrain genuinely appears - a CSS
 * animation here fires on a map load or a build and never on an orbit.
 * The delay comes from where the solid STANDS, so a line crosses the
 * arena rather than the screen.
 */
export function SceneFaces({ faces, selectedSolidId, animation }: SceneFacesProps) {
  const arrived = useArrivalLedger(faces, animation);
  const animating = animation !== 'none';
  return faces.map((face) => (
    <polygon
      key={face.key}
      {...faceHitAttrs(face)}
      className={clsx(
        styles.face,
        face.solidId === selectedSolidId && styles.faceSelected,
        animating && styles.faceArriving,
        MOTION[animation],
      )}
      style={animating ? ({ '--sweep': `${arrived.get(face.key) ?? 0}ms` } as React.CSSProperties) : undefined}
      points={toPoints(face)}
      fill={faceFill(face)}
      fillOpacity={faceOpacity(face)}
      stroke={faceFill(face)}
    >
      {face.label ? <title>{face.label}</title> : null}
    </polygon>
  ));
}

/**
 * The hatching on dangerous and impassable ground, painted AFTER every
 * face so a hazard is never buried under the ground it marks. Pointer
 * events off: it is a warning, not a target.
 */
export function TerrainMarks({ faces }: { faces: SceneFace[] }) {
  const tops = faces.filter((face) => face.kind === 'top');
  return (
    <>
      {tops
        .filter((face) => face.hazard)
        .map((face) => (
          <polygon
            key={`hazard-${face.key}`}
            data-testid={`hazard-${face.solidId}`}
            className={styles.hazardMark}
            points={toPoints(face)}
            fill="url(#hazard-hatch)"
          />
        ))}
      {tops
        .filter((face) => face.blocked)
        .map((face) => (
          <polygon
            key={`blocked-${face.key}`}
            data-testid={`blocked-${face.solidId}`}
            className={styles.blockedMark}
            points={toPoints(face)}
            fill="url(#blocked-hatch)"
          />
        ))}
    </>
  );
}

/**
 * The hatch patterns, defined once and referenced by every marked face. A
 * hazard does nothing mechanically, so being SEEN is the whole of its job -
 * and a colour alone cannot do that, because it competes with every
 * material on the map. Blocked ground is a cross-hatch, which reads as
 * "no" the way stripes read as "careful". Rects, not <line>s - the grid
 * is counted by tag name.
 */
export function SceneDefs() {
  return (
    <defs>
      <pattern id="hazard-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="3" height="8" fill="#fbbf24" fillOpacity="0.8" />
      </pattern>
      <pattern id="blocked-hatch" width="10" height="10" patternUnits="userSpaceOnUse">
        <rect x="0" y="4" width="10" height="1.6" fill="#94a3b8" fillOpacity="0.85" />
        <rect x="4" y="0" width="1.6" height="10" fill="#94a3b8" fillOpacity="0.85" />
      </pattern>
    </defs>
  );
}
